import { Injectable, signal } from '@angular/core';
import { BusinessCalendarEvent } from '../../models/calendar.model';
import { CalendarEvent, CalendarView } from 'angular-calendar';

@Injectable({
  providedIn: 'root',
})
export class CalendarHelper {
  selectedEvent = signal<BusinessCalendarEvent | null>(null);
  selectedOccurrenceStart = signal<Date | null>(null);
  selectedOccurrenceEnd = signal<Date | null>(null);

  selectEvent(businessEvent: BusinessCalendarEvent, calendarEvent?: CalendarEvent): void {
    this.selectedEvent.set(businessEvent);
    this.selectedOccurrenceStart.set(
      calendarEvent?.start ?? new Date(businessEvent.started_at)
    );
    this.selectedOccurrenceEnd.set(
      calendarEvent?.end ?? new Date(businessEvent.ended_at)
    );
  }

  clearSelectedEvent(): void {
    this.selectedEvent.set(null);
    this.selectedOccurrenceStart.set(null);
    this.selectedOccurrenceEnd.set(null);
  }

  isSelectedOccurrence(calendarEvent: CalendarEvent): boolean {
    const selectedEvent = this.selectedEvent();
    const selectedStart = this.selectedOccurrenceStart();

    if (!selectedEvent || !selectedStart || selectedEvent.id !== calendarEvent.id) {
      return false;
    }

    return selectedStart.getTime() === calendarEvent.start.getTime();
  }

  hasNeighboringEvents(event: CalendarEvent, events: CalendarEvent[] = [], calendarView: CalendarView = CalendarView.Week, acceptableNeighbors = 1): boolean {
    if (!event || events.length === 0 || acceptableNeighbors < 1 || ![CalendarView.Week, CalendarView.Day].includes(calendarView)) {
      return false;
    }

    const eventSegments = this.getEventHourSegments(event);

    const maxNeighborCount = Math.max(
      ...eventSegments.map((segmentDate) =>
        events.filter((calendarEvent) =>
          !this.isSameCalendarEvent(calendarEvent, event)
          && this.eventOverlapsHourSegment(calendarEvent, segmentDate)
        ).length
      )
    );

    if (calendarView === CalendarView.Week) {
      return maxNeighborCount > acceptableNeighbors;
    } else {
      // for day view
      return maxNeighborCount > (acceptableNeighbors * 7);
    }
  }

  private getEventHourSegments(event: CalendarEvent): Date[] {
    const eventStart = event.start.getTime();
    const eventEnd = event.end?.getTime() ?? eventStart;
    const segmentDate = this.getHourSegmentDate(event.start);
    const lastSegmentDate = this.getHourSegmentDate(
      new Date(eventEnd === eventStart ? eventEnd : eventEnd - 1)
    );
    const segments: Date[] = [];

    while (segmentDate.getTime() <= lastSegmentDate.getTime()) {
      segments.push(new Date(segmentDate));
      segmentDate.setHours(segmentDate.getHours() + 1);
    }

    return segments;
  }

  private eventOverlapsHourSegment(
    event: CalendarEvent,
    segmentDate: Date
  ): boolean {
    const segmentStart = segmentDate.getTime();
    const segmentEnd = segmentStart + 60 * 60 * 1000;
    const eventStart = event.start.getTime();
    const eventEnd = event.end?.getTime() ?? eventStart;

    if (eventStart === eventEnd) {
      return eventStart >= segmentStart && eventStart < segmentEnd;
    }

    return eventStart < segmentEnd && eventEnd > segmentStart;
  }

  private getHourSegmentDate(date: Date): Date {
    const segmentDate = new Date(date);
    segmentDate.setMinutes(0, 0, 0);

    return segmentDate;
  }

  private isSameCalendarEvent(
    firstEvent: CalendarEvent,
    secondEvent: CalendarEvent
  ): boolean {
    if (firstEvent.id !== undefined && secondEvent.id !== undefined) {
      return (
        firstEvent.id === secondEvent.id &&
        firstEvent.start.getTime() === secondEvent.start.getTime()
      );
    }

    return firstEvent === secondEvent;
  }
}
